import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

export type PermissionCatalogEntry = {
  key: string
  label: string
  description: string
  group: string
}

// One tenant the user belongs to, plus the role they hold THERE.
export type TenantRoleAssignment = {
  tenantId: string
  roleId: string | null
  roleSlug: string | null
  roleDisplayName: string | null
}

export type ManagedUser = {
  id: string
  email: string
  username: string | null
  // The parts are what an edit form binds to; `fullName` is derived from them
  // server-side and is what every display path reads. Both are nullable because
  // users created before the name parts existed have neither.
  firstName: string | null
  lastName: string | null
  fullName: string | null
  enabled: boolean
  /** Signs in through SSO rather than a password. A flag only, for now. */
  ssoOnly: boolean
  /** Lab role: Supervisor (true) or Tech. Global, not per tenant. */
  isSupervisor: boolean
  /** Who this person reports to. Global, like the lab role. */
  supervisorId: string | null
  // Platform-wide access. Replaces the old global role.
  isSuperAdmin: boolean
  tenantRoles: TenantRoleAssignment[]
  tenantIds: string[]
}

// A membership to create, with the role in that tenant. `roleId` omitted means
// "member with no role there", i.e. no permissions until one is set.
export type TenantMembershipInput = { tenantId: string; roleId?: string }

// Both names are required — there is no `fullName` input: the server derives
// the display name from the parts, so sending one would let the two disagree.
export type CreateUserPayload = {
  email: string
  firstName: string
  lastName: string
  temporaryPassword: string
  enabled?: boolean
  isSuperAdmin?: boolean
  tenants?: TenantMembershipInput[]
}

async function request<T>(
  path: string,
  init?: RequestInit & { authenticated?: boolean },
): Promise<T> {
  const authenticated = init?.authenticated !== false
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authenticated
      ? authHeaders(init?.headers)
      : { 'Content-Type': 'application/json', ...(init?.headers as Record<string, string> | undefined) },
  })
  if (!res.ok) {
    if (authenticated) handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      message = (JSON.parse(text) as { message?: string }).message ?? text
    } catch {
      // keep raw text
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const usersApi = {
  /**
   * `search` matches email, username and the name columns, server-side.
   *
   * Server-side rather than filtering the loaded page: this list is paginated,
   * so a client-side filter would only ever search the rows already fetched and
   * would report "no results" for someone sitting on page three.
   */
  list: (
    params: { skip?: number; take?: number; search?: string; sort?: string } = {},
  ): Promise<{ items: ManagedUser[]; total: number }> => {
    const qs = new URLSearchParams()
    if (params.skip !== undefined) qs.set('skip', String(params.skip))
    if (params.take !== undefined) qs.set('take', String(params.take))
    const search = params.search?.trim()
    if (search) qs.set('search', search)
    // Column sort, applied server-side before paging (z8nrz7e4h6).
    if (params.sort) qs.set('sort', params.sort)
    const s = qs.toString()
    return request<{ items: ManagedUser[]; total: number }>(`/users${s ? `?${s}` : ''}`)
  },

  /**
   * Import existing Cognito users into the RBAC user table.
   *
   * Until this has been run at least once the `User` table can be empty, which
   * leaves every signed-in principal with `userId: null` — assignment and any
   * other feature keyed to a DB user then fails for everyone.
   */
  syncFromCognito: () =>
    request<{ imported: number; linked: number; total: number }>('/users/sync', {
      method: 'POST',
    }),

  permissionsCatalog: () => request<PermissionCatalogEntry[]>('/users/permissions/catalog'),

  // Resolve email-or-username → login email (public; called before sign-in).
  // `method: 'sso'` = SSO-only user; `idp` = the enabled IdP owning the email domain.
  resolveLogin: (identifier: string) =>
    request<{ email: string; method?: 'sso' | 'password'; idp?: string }>('/users/resolve-login', {
      method: 'POST',
      body: JSON.stringify({ identifier }),
      authenticated: false,
    }),

  // Self-service: update own name/username.
  //
  // The name fields are optional in the PAYLOAD but not in the RESULT: the
  // server refuses any save that would leave the account without both parts,
  // so a username-only PATCH still 400s for a user who has never had a name.
  updateMe: (payload: { firstName?: string; lastName?: string; username?: string }) =>
    request<ManagedUser>('/users/me', { method: 'PATCH', body: JSON.stringify(payload) }),

  // Self-service: change own password.
  changePassword: (previousPassword: string, proposedPassword: string) =>
    request<{ ok: true }>('/users/me/password', {
      method: 'POST',
      body: JSON.stringify({ previousPassword, proposedPassword }),
    }),

  // Admin: set a temporary password for another user. The user must choose a
  // new one at next sign-in.
  resetPassword: (id: string, temporaryPassword: string) =>
    request<{ ok: true }>(`/users/${id}/password`, {
      method: 'POST',
      body: JSON.stringify({ temporaryPassword }),
    }),

  // Admin: update another user's name/username. Same result-not-payload rule as
  // `updateMe`.
  updateProfile: (
    id: string,
    payload: { firstName?: string; lastName?: string; username?: string },
  ) =>
    request<ManagedUser>(`/users/${id}/profile`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  create: (payload: CreateUserPayload) =>
    request<ManagedUser>('/users', { method: 'POST', body: JSON.stringify(payload) }),

  setSuperAdmin: (id: string, isSuperAdmin: boolean) =>
    request<ManagedUser>(`/users/${id}/super-admin`, {
      method: 'PATCH',
      body: JSON.stringify({ isSuperAdmin }),
    }),

  setEnabled: (id: string, enabled: boolean) =>
    request<ManagedUser>(`/users/${id}/enabled`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),

  /** Lab role and/or who they report to (superadmin; both global). */
  setSupervisor: (id: string, payload: { isSupervisor?: boolean; supervisorId?: string | null }) =>
    request<ManagedUser>(`/users/${id}/supervisor`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  setSsoOnly: (id: string, ssoOnly: boolean) =>
    request<ManagedUser>(`/users/${id}/sso-only`, {
      method: 'PATCH',
      body: JSON.stringify({ ssoOnly }),
    }),

  // Full replace: the array becomes the user's memberships, each with its role.
  setMemberships: (id: string, tenants: TenantMembershipInput[]) =>
    request<ManagedUser>(`/users/${id}/memberships`, {
      method: 'PATCH',
      body: JSON.stringify({ tenants }),
    }),
}
